import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph} from '@/components/ui/icons'
import {Input} from '@/components/ui/input'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {useMunicipioSearch, type Municipio} from '@/hooks/salud/perfil_riesgo/use-perfil'
import {tope} from '@/lib/campos-texto'

function TriggerLabel({value, placeholder}: {value: Municipio | null; placeholder: string}) {
  if (!value) return <span className="min-w-0 truncate text-muted-foreground">{placeholder}</span>
  return (
    <span className="min-w-0 truncate">
      {value.nombre}
      <span className="text-muted-foreground">, {value.departamento}</span>
    </span>
  )
}

/** En `components/` porque lo usan dos features y `dependency-cruiser` prohíbe importar entre ellas. */
export function MunicipioPicker({
  businessId,
  value,
  disabled,
  onPick,
}: {
  businessId: string
  value: Municipio | null
  disabled: boolean
  onPick: (municipio: Municipio) => void
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 200)
  const {data: results} = useMunicipioSearch(businessId, debouncedQuery)

  function pick(municipio: Municipio) {
    onPick(municipio)
    setOpen(false)
  }

  function handleOpenChange(next: boolean) {
    if (disabled) return
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <SearchComboboxShell
      title={t('perfilRiesgo.facts.municipioSelect')}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          data-testid="municipio-trigger"
          className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-60 md:h-9">
          <TriggerLabel value={value} placeholder={t('perfilRiesgo.facts.municipioSelect')} />
          <ChevronGlyph size={14} className="opacity-60" />
        </button>
      }>
      <Input
        maxLength={tope('CIU')}
        placeholder={t('perfilRiesgo.facts.municipioSearchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        autoFocus
        data-testid="municipio-search"
        className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
      />
      <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
        {query.trim() &&
          results?.items.map((municipio) => (
            <li key={municipio.code}>
              <button
                type="button"
                className="w-full break-words rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
                onClick={() => pick(municipio)}>
                <span>{municipio.nombre}</span>{' '}
                <span className="text-muted-foreground">— {municipio.departamento}</span>
              </button>
            </li>
          ))}
        {query.trim() && results?.items.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('perfilRiesgo.facts.municipioNoResults')}</li>
        )}
      </ul>
    </SearchComboboxShell>
  )
}
